import { ApolloError, DefaultContext, useMutation, useQuery } from '@apollo/client'
import { defaultTo } from 'ramda'
import { useContext, useEffect, useState } from 'react'

import {
  ApplyCustomerStoreCreditDocument,
  CartFragment,
  GetCustomerCartDocument,
  GetCustomerCartQuery,
} from '../../../graphql/magento'
import {
  CustomerAvailableBalanceDocument,
  CustomerAvailableBalanceQuery,
} from '../../../graphql/search'
import { TokenContext } from '../../../layouts/context'
import { testKitDelayedBilling } from '../../../utils/buy/paymentTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../../utils/graphqlTools'
import { ApolloClientsContext } from '../../graphql/apollo'
import { useTestKitProducts } from '../../products/hooks/useTestKitProducts'
import { getEligibleRebateAmount } from '../utils'
import { useCartContext } from './useCartContext'

export interface UseCartRebateValidation {
  loading: boolean
  error: Error | ApolloError | null
}

const isCartLoaded = (
  customerCart: GetCustomerCartQuery['customerCart'] | undefined,
): customerCart is CartFragment => {
  return customerCart != null
}

const isAvailableBalanceLoaded = (
  data: CustomerAvailableBalanceQuery | null | undefined,
  loading: boolean,
  called: boolean,
): data is {
  customer: {
    availableBalance: NonNullable<
      NonNullable<CustomerAvailableBalanceQuery['customer']>['availableBalance']
    >
  }
} => {
  return called && !loading && !!data?.customer?.availableBalance
}

const defaultToZero = defaultTo(0)

export const useCartRebateValidation = (): UseCartRebateValidation => {
  const { comma: commaClient } = useContext(ApolloClientsContext)
  const token = useContext(TokenContext)
  const { isTestKitSku } = useTestKitProducts()
  // skip loading state if token not available (e.g. guest user)
  // - this allows child components to redirect guest users to login/register
  const [loading, setLoading] = useState<boolean>(!!token)
  const [error, setError] = useState<Error | ApolloError | null>(null)

  const { cart } = useCartContext()

  const {
    data,
    loading: loadingAvailableBalance,
    called: calledAvailableBalance,
    error: errorAvailableBalance,
  } = useQuery(CustomerAvailableBalanceDocument, {
    client: commaClient,
    context: { token } as DefaultContext,
    fetchPolicy: 'cache-and-network',
    // avoid error response if not authenticated
    skip: !token,
  })
  const [applyStoreCredit] = useMutation(ApplyCustomerStoreCreditDocument, {
    context: { token },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetCustomerCartDocument,
        context: { token },
      },
    ],
  })

  useEffect(() => {
    if (
      isCartLoaded(cart) &&
      isAvailableBalanceLoaded(data, loadingAvailableBalance, calledAvailableBalance)
    ) {
      const {
        customer: { availableBalance },
      } = data
      // Destructure and default to 0 if any balance is undefined
      const { totalBalance = { amount: 0 } } = availableBalance

      const delayedBilling = testKitDelayedBilling({ cart, isTestKitSku })

      // Calculate the maximum rebate amount based on the available balances
      const maxRebateAmount = defaultToZero(
        getEligibleRebateAmount({
          cart,
          currentBalance: Number(totalBalance.amount),
          delayedBilling,
        }),
      )

      const { id: cartId, rebate } = cart
      const rebateApplied = rebate != null ? Number(rebate.value) : 0

      // If the applied rebate is less than or equal to the max rebate amount, return early
      if (rebateApplied <= maxRebateAmount) {
        setLoading(false)
        return
      }

      applyStoreCredit({
        variables: {
          cartId,
          amount: maxRebateAmount,
        },
      })
        .then(checkAndDiscardQueryResponse)
        .catch((err) => {
          logAndCaptureException(err)
          setError(err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [applyStoreCredit, calledAvailableBalance, cart, data, isTestKitSku, loadingAvailableBalance])

  useEffect(() => {
    if (errorAvailableBalance) {
      logAndCaptureException(errorAvailableBalance)
      setError(errorAvailableBalance)
      setLoading(false)
    }
  }, [errorAvailableBalance])

  return { loading, error }
}
